import type { Organizer, OrganizerMember, OrganizerMemberRole } from '~/types/domain'

export const useOrganizerStore = defineStore('organizer', () => {
  const organizers = ref<Organizer[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const nextKey = ref<string | null>(null)
  const hasMore = computed(() => nextKey.value !== null)

  function getById(id: string) {
    return organizers.value.find(o => o.id === id)
  }

  function getOrganizerName(id: string) {
    return getById(id)?.name ?? 'Unknown'
  }

  function upsert(org: Organizer) {
    const idx = organizers.value.findIndex(o => o.id === org.id)
    const merged = { ...org, members: org.members ?? [] }
    if (idx >= 0) {
      organizers.value[idx] = { ...organizers.value[idx]!, ...merged }
    } else {
      organizers.value.push(merged)
    }
  }

  async function fetchAll(params?: { status?: string, limit?: number, append?: boolean }) {
    const { $api } = useNuxtApp()
    isLoading.value = true
    error.value = null
    try {
      const query = new URLSearchParams()
      if (params?.status) query.set('status', params.status)
      if (params?.limit) query.set('limit', String(params.limit))
      if (params?.append && nextKey.value) query.set('next_key', nextKey.value)

      const qs = query.toString()
      const res = await $api<{ organizers: Organizer[], count: number, nextKey: string | null }>(`/organizers${qs ? `?${qs}` : ''}`)

      if (!params?.append) {
        organizers.value = []
      }
      for (const org of res.organizers) {
        upsert(org)
      }
      nextKey.value = res.nextKey ?? null
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to load organizers'
    } finally {
      isLoading.value = false
    }
  }

  async function fetchMore(params?: { status?: string, limit?: number }) {
    if (!hasMore.value) return
    await fetchAll({ ...params, append: true })
  }

  async function fetchById(id: string) {
    const { $api } = useNuxtApp()
    try {
      const org = await $api<Organizer>(`/organizers/${id}`)
      upsert(org)
      return organizers.value.find(o => o.id === id)
    } catch {
      return undefined
    }
  }

  async function fetchMembers(orgId: string) {
    const { $api } = useNuxtApp()
    try {
      const res = await $api<{ members: Array<{ userId: string, displayName: string, email: string, role: OrganizerMemberRole, joinedAt: string }>, count: number }>(`/organizers/${orgId}/members`)
      const mapped: OrganizerMember[] = res.members.map(m => ({
        userId: m.userId,
        name: m.displayName,
        email: m.email,
        role: m.role,
        joinedAt: m.joinedAt
      }))
      const org = getById(orgId)
      if (org) {
        org.members = mapped
      }
      return mapped
    } catch {
      return []
    }
  }

  async function register(data: { name: string, description?: string, contactEmail: string, displayName: string }) {
    const { $api } = useNuxtApp()
    isLoading.value = true
    error.value = null
    try {
      const res = await $api<{ id: string, name: string, status: string }>('/organizers', {
        method: 'POST',
        body: { ...data, description: data.description ?? '' }
      })
      // Fetch full organizer to get all fields
      await fetchById(res.id)
      return res
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Registration failed'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function updateProfile(id: string, data: Partial<Pick<Organizer, 'name' | 'contactEmail' | 'contactPhone' | 'description'>> & { lineUrl?: string }) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${id}`, { method: 'PUT', body: data })
      await fetchById(id)
      return true
    } catch {
      return false
    }
  }

  async function verify(id: string) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${id}/verify`, { method: 'POST' })
      await fetchById(id)
      return true
    } catch {
      return false
    }
  }

  async function suspend(id: string) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${id}/suspend`, { method: 'POST' })
      await fetchById(id)
      return true
    } catch {
      return false
    }
  }

  async function reactivate(id: string) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${id}/reactivate`, { method: 'POST' })
      await fetchById(id)
      return true
    } catch {
      return false
    }
  }

  async function deleteOrganizer(id: string) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${id}`, { method: 'DELETE' })
      organizers.value = organizers.value.filter(o => o.id !== id)
      return true
    } catch {
      return false
    }
  }

  async function addMember(orgId: string, data: { userId: string, displayName: string, email: string, role: OrganizerMemberRole }) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${orgId}/members`, { method: 'POST', body: data })
      await fetchMembers(orgId)
      return true
    } catch {
      return false
    }
  }

  async function removeMember(orgId: string, userId: string) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${orgId}/members/${userId}`, { method: 'DELETE' })
      await fetchMembers(orgId)
      return true
    } catch {
      return false
    }
  }

  async function updateMemberRole(orgId: string, userId: string, newRole: OrganizerMemberRole) {
    const { $api } = useNuxtApp()
    try {
      await $api(`/organizers/${orgId}/members/${userId}`, {
        method: 'PATCH',
        body: { role: newRole }
      })
      await fetchMembers(orgId)
      return true
    } catch {
      return false
    }
  }

  return {
    organizers,
    isLoading,
    error,
    nextKey,
    hasMore,
    getById,
    getOrganizerName,
    fetchAll,
    fetchMore,
    fetchById,
    fetchMembers,
    verify,
    suspend,
    reactivate,
    deleteOrganizer,
    register,
    updateProfile,
    addMember,
    removeMember,
    updateMemberRole
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrganizerStore, import.meta.hot))
}
